<template>
  <div>
    <div class="nowrap overflow-auto"
         v-if="showChecks">
      <CheckboxGroup v-for="c in patientCommentTypes"
                     :value="checked"
                     :checkboxvalue="c.id"
                     class="d-inline-block mr-3"
                     :key="`check-${c.id}`"
                     :fid="`check-${c.id}`"
                     @input="(...args) => checkboxInput(c.id, ...args)"
                     :label="$t(c.title)"/>
    </div>

    <div v-if="!comments.length && loaded">
      {{ $t('no_data_available') }}
    </div>

    <div v-if="comments.length && showSortingByDate" class="text-right mt-3">
      <div class="pointer mb-2 small text-muted text-underlined"
          @click="changeSorting">
        {{ sort === 'desc' ? $t('first_new') : $t('first_old') }}
        <ArrowDownSvg class="ml-2" :class="{ 'arrow-reverted': sort === 'asc' }"/>
      </div>
    </div>

    <PatientCommentTeaser v-for="c in comments"
                          :key="`comment-${c.id}`"
                          :comment="c"
                          :show-images="showImages"
                          @getComments="getComments(true)"
                          @repinnedComment="(comment) => emit('repinnedComment', comment)"
                          class="mb-3">
    </PatientCommentTeaser>
    <div v-if="hasMore"
         class="mb-3">
      <button :disabled="loading"
              class="btn btn-themed btn-themed-outline btn-themed-squared"
              @click="loadMore">
        {{ $t('load_more') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import PatientCommentTeaser from "@/components/parts/patients/PatientCommentTeaser.vue"
import {ref, computed, onMounted, onBeforeUnmount, watch } from "vue"
import { useStore } from "vuex"
import { EloquentService } from "@/services/api/eloquent.api.service"
import CheckboxGroup from "@/components/form/CheckboxGroup.vue"
import { patientCommentTypes } from "@/dictionaries/dictionary"
import {copyObject} from "@/extensions/prototypes/prototypes"
import ArrowDownSvg from '@/assets/svg-vue/general/arrow_down.svg'

const props = defineProps({
  showImages: Boolean,
  patient: Object,
  checked: {
    type: Array,
    default: () => []
  },
  showChecks: {
    type: Boolean,
    default: false
  },
  showSortingByDate: {
    type: Boolean,
    default: false
  }
})

const store = useStore()

const loading = computed(() => store.state.dom.loading)
const counter = computed(() => store.state.datatable.dataTableCounter)
const dataTableWatcher = ref(true)
const loaded = ref(false)
const page = ref(1)
const paginator = ref(null)
const comments = ref([])
const sort = ref('desc')

const emit = defineEmits(['checksChanged', 'repinnedComment'])

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  getComments(true)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})

const getComments = async (flush = false) => {
  if(!props.patient) return
  if(flush) page.value = 1
  const res = await EloquentService.dataTable('patient_comment', page.value, {
    pagination: 10,
    order: 'updated_at',
    sort: sort.value,
    condition: {
      user_id: props.patient.id,
      comment_type: props.checked
    },
  })
  if(flush) comments.value = []
  comments.value = comments.value.concat(res.data.result.data)
  paginator.value = res.data.result
  loaded.value = true
}

const handleScroll = () => {
  if(!loaded.value || loading.value || !hasMore.value) return
  const bottomOfWindow = (Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop) + window.innerHeight) > (document.documentElement.offsetHeight - 40)
  if (bottomOfWindow) {
    loadMore()
  }
}

const hasMore = computed(() => {
  if(!paginator.value) return false
  return page.value < paginator.value.last_page
})

const loadMore = () => {
  page.value++
  getComments()
}

const checkboxInput = async (clicked, all) => {
  if(clicked === 'all' && all.includes('all')) {
    emit('checksChanged', copyObject(patientCommentTypes.map(x => x.id)))
    return
  }
  if(clicked === 'all' && !all.includes('all')) {
    emit('checksChanged', [])
    return
  }
  const tmpChecked = all.filter(x => x !== 'all')
  if(tmpChecked.length === (patientCommentTypes.length - 1)) {
    tmpChecked.push('all')
  }
  emit('checksChanged', tmpChecked)
}

const changeSorting = () => {
  if(loading.value) return
  sort.value = sort.value === 'desc' ? 'asc' : 'desc'
  getComments(true)
}

watch(counter, () => {
  if(dataTableWatcher.value) getComments(true)
})

watch(() => props.checked, () => {
  getComments(true)
}, {
  deep: true,
})

defineExpose({
  getComments
})
</script>

<style scoped>
.arrow-reverted {
  transform: rotate(-180deg);
}
</style>
